// src/components/SetupForm.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  doc,
  getDoc,
  addDoc,
  updateDoc,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { useTranslation } from 'react-i18next';

// --- IMPORT MUI ---
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

// --- IMPORTA I NUOVI MODULI "LEGO" ---
import CarCentricFormView from './setup_car_centric/CarCentricFormView';

// --- DEFINISCI IL NUOVO STATO INIZIALE COMPLETO ---
const newInitialSetupState = {
  meteo: { tempAria: '', tempAsfalto: '', umidita: '' },
  feedback: {
    noteTecnico: '',
    feedbackPilota: '',
    bestLap: '',
    listaTempi: '',
    linkTelemetria: '',
    descrizioneTelemetria: '',
    linkVideo: '',
    descrizioneVideo: ''
  },
  anteriore: {
    convergenzaSX: '', toeInOrOutSX: 'out',
    convergenzaDX: '', toeInOrOutDX: 'out',
    camberSX: '', camberDX: '',
    casterSX: '', casterDX: '',
    altezzaTargetSX: '', altezzaTargetDX: '',
    altezzaMeasuredSX: '', altezzaMeasuredDX: '',
    mollaSX: '', mollaDX: '',
    barra: '',
    rigidezzaBumpstopSX: '', distanzaBumpstopSX: '',
    rigidezzaBumpstopDX: '', distanzaBumpstopDX: '',
    ammortizzatori: {
      bumpSX: '', reboundSX: '', bumpDX: '', reboundDX: '',
      fastBumpSX: '', fastReboundSX: '',
      fastBumpDX: '', fastReboundDX: ''
    }
  },
  posteriore: {
    convergenzaSX: '', toeInOrOutSX: 'in',
    convergenzaDX: '', toeInOrOutDX: 'in',
    camberSX: '', camberDX: '',
    altezzaTargetSX: '', altezzaTargetDX: '',
    altezzaMeasuredSX: '', altezzaMeasuredDX: '',
    mollaSX: '', mollaDX: '',
    barra: '',
    rigidezzaBumpstopSX: '', distanzaBumpstopSX: '',
    rigidezzaBumpstopDX: '', distanzaBumpstopDX: '',
    ammortizzatori: {
      bumpSX: '', reboundSX: '', bumpDX: '', reboundDX: '',
      fastBumpSX: '', fastReboundSX: '',
      fastBumpDX: '', fastReboundDX: ''
    }
  },
  gomme_ant_sx: { pFreddo: '', pCaldoTarget: '', pCaldoMeasured: '', pCaldoAdjusted: '', tInt: '', tMed: '', tExt: '' },
  gomme_ant_dx: { pFreddo: '', pCaldoTarget: '', pCaldoMeasured: '', pCaldoAdjusted: '', tInt: '', tMed: '', tExt: '' },
  gomme_post_sx: { pFreddo: '', pCaldoTarget: '', pCaldoMeasured: '', pCaldoAdjusted: '', tInt: '', tMed: '', tExt: '' },
  gomme_post_dx: { pFreddo: '', pCaldoTarget: '', pCaldoMeasured: '', pCaldoAdjusted: '', tInt: '', tMed: '', tExt: '' },
  aero: {
    alaPosteriore: '',
    splitterAnteriore: '',
    condottiFrenoAnteriori: '',
    condottiFrenoPosteriori: ''
  },
  differenziale: {
    precarico: '', bloccaggio: '',
    fuelConsumption: '',
    gearRatios: ''
  },
  weights: {
    dryWeight: '',
    fuelLiters: '',
    totalWeight: '',
    totalFrontWeight: '',
    totalRearWeight: '',
    cornerFL: '', cornerFR: '', cornerRL: '', cornerRR: '',
    weightRip: '', driverWeight: '',
    minRegWeight: '',
    ballast: ''
  },
  elettronica: {
    tc1: '', tc2: '', abs: '', mappaECU: ''
  },
  freni: {
    potenzaFreni: '', bilanciamentoFreni: '', rapportoSterzo: '',
    pastiglieFrenoAnteriori: '', pastiglieFrenoPosteriori: ''
  },
  strategia: {
    carburanteIniziale: '', strategiaPit: ''
  }
};

// --- FUNZIONI HELPER ---
const formatDateForInput = (date) => {
  if (!date) return '';
  const d = date.toDate ? date.toDate() : new Date(date);
  if (isNaN(d)) return '';
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().split('T')[0];
};
const formatDate = (date, lang) => {
  if (!date) return 'N/A';
  const d = date.toDate ? date.toDate() : new Date(date);
  const locale = lang === 'it' ? 'it-IT' : 'en-GB';
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const formatSessionTime = (timestamp, lang) => {
  if (!timestamp) return '';
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const locale = lang === 'it' ? 'it-IT' : 'en-GB';
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};
const rowStyle = { display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 };
const FlexTextField = ({ label, value, onChange, ...props }) => (
  <TextField
    label={label}
    value={value}
    onChange={onChange}
    fullWidth
    {...props}
    sx={{ ...props.sx, flex: 1 }}
  />
);
const accordionStyle = {
  elevation: 0,
  disableGutters: true,
  sx: {
    elevation: 0,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    '&:before': { display: 'none' },
    mb: 1 // Spaziatura tra i moduli
  }
};
// --- FINE HELPER ---


// --- COMPONENTE INTERNO PER INFO SESSIONE ---
const SetupFormSessionInfo = React.memo(({
  t,
  eventsList,
  isLoadingEvents,
  selectedEventId,
  handleEventChange,
  eventSessions,
  isLoadingSessions,
  selectedSessionId,
  handleSessionChange,
  selectedEvent,
  currentLang,
  dataSessione,
  setDataSessione,
  driversList,
  isLoadingDrivers,
  selectedDriver,
  handleDriverChange,
  meteoData,
  handleMeteoChange
}) => {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>{t('section_session_info')}</Typography>
      <Box sx={rowStyle}>
        <FormControl fullWidth required sx={{ flex: 1 }}>
          <InputLabel id="event-select-label">{t('field_event')}</InputLabel>
          <Select labelId="event-select-label" value={selectedEventId} label={t('field_event')} onChange={handleEventChange} disabled={isLoadingEvents}>
            {eventsList.length === 0 && <MenuItem value="" disabled><em>{t('field_event_loading')}</em></MenuItem>}
            {eventsList.map((event) => (<MenuItem key={event.id} value={event.id}>{event.eventName} ({formatDate(event.startDate, currentLang)})</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl fullWidth required sx={{ flex: 1 }} disabled={!selectedEventId || isLoadingSessions}>
          <InputLabel id="session-select-label">{t('field_session')}</InputLabel>
          <Select labelId="session-select-label" value={selectedSessionId} label={t('field_session')} onChange={handleSessionChange}>
            {eventSessions.length === 0 && <MenuItem value="" disabled><em>{t('field_session_loading')}</em></MenuItem>}
            {eventSessions.map((session) => (<MenuItem key={session.id} value={session.id}>{session.sessionName} ({formatSessionTime(session.startTime, currentLang)})</MenuItem>))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={rowStyle}>
        <FlexTextField label={t('field_track')} value={selectedEvent?.trackName || ''} disabled InputLabelProps={{ shrink: !!selectedEvent?.trackName }} />
        <FlexTextField label={t('field_date')} type="date" value={dataSessione} onChange={(e) => setDataSessione(e.target.value)} InputLabelProps={{ shrink: true }} required />
        <Autocomplete
          options={driversList}
          getOptionLabel={(option) => option.nomeCompleto || ''}
          value={selectedDriver}
          disabled={isLoadingDrivers}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={handleDriverChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('field_driver')}
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingDrivers ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ flex: 1 }}
        />
      </Box>
      <Box sx={rowStyle}>
        <FlexTextField label={t('field_air_temp')} type="number" value={meteoData.tempAria} onChange={(e) => handleMeteoChange('tempAria', e.target.value)} />
        <FlexTextField label={t('field_track_temp')} type="number" value={meteoData.tempAsfalto} onChange={(e) => handleMeteoChange('tempAsfalto', e.target.value)} />
        <FlexTextField label={t('field_humidity')} type="number" inputProps={{ min: 0, max: 100 }} value={meteoData.umidita} onChange={(e) => handleMeteoChange('umidita', e.target.value)} />
      </Box>
    </Box>
  );
});
// --- FINE COMPONENTE INFO SESSIONE ---


// --- IL NUOVO COMPONENTE "CONTENITORE" ---
function SetupForm({ teamId, userRole }) {
  const { t, i18n } = useTranslation('setup');
  const currentLang = i18n.language;

  const { vehicleId, setupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // --- CORREZIONE: Definisci isDuplicating qui ---
  const duplicateFromId = location.state?.duplicateFromId;
  const isEditing = Boolean(setupId) && !Boolean(duplicateFromId);
  const isDuplicating = Boolean(duplicateFromId); // <-- ORA È DEFINITO
  // --- FINE CORREZIONE ---

  // Stati del Form
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [selectedSessionName, setSelectedSessionName] = useState('');
  const [dataSessione, setDataSessione] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [newSetupData, setNewSetupData] = useState(newInitialSetupState);

  // Stati di Caricamento
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAccessBlocked, setIsAccessBlocked] = useState(true);

  // Stati Dati (Liste)
  const [eventsList, setEventsList] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [eventSessions, setEventSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [driversList, setDriversList] = useState([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(true);

  // Stati Veicolo e Template
  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [setupTemplate, setSetupTemplate] = useState(null); // Il template con l'array `modules`
  const [isLoadingVehicleAndTemplate, setIsLoadingVehicleAndTemplate] = useState(true);

  // --- GESTIONE DATI ---

  // Carica Veicolo e Template associato
  useEffect(() => {
    if (!vehicleId || !teamId) return;
    setIsLoadingVehicleAndTemplate(true);
    getDoc(doc(db, 'vehicles', vehicleId)).then(vehicleSnap => {
      if (vehicleSnap.exists() && vehicleSnap.data().teamId === teamId) {
        const vehicleData = { id: vehicleSnap.id, ...vehicleSnap.data() };
        setCurrentVehicle(vehicleData);
        if (vehicleData.templateId) {
          getDoc(doc(db, 'setupTemplates', vehicleData.templateId)).then(templateSnap => {
            if (templateSnap.exists()) {
              setSetupTemplate(templateSnap.data());
            } else { setSetupTemplate(null); } // Template non trovato
            setIsLoadingVehicleAndTemplate(false);
          });
        } else { setIsLoadingVehicleAndTemplate(false); } // Veicolo non ha template
      } else { setIsLoadingVehicleAndTemplate(false); } // Veicolo non trovato
    }).catch(err => setIsLoadingVehicleAndTemplate(false));
  }, [vehicleId, teamId]);

  // Carica Eventi e Piloti (in parallelo)
  useEffect(() => {
    // Bug fix: setta i flag a false anche su return precoce, altrimenti il render
    // gated da isPageLoading rimane bloccato nello spinner per i team nuovi.
    if (!teamId) { setIsLoadingDrivers(false); setIsLoadingEvents(false); return; }
    setIsLoadingDrivers(true);
    setIsLoadingEvents(true);
    // Timeout di sicurezza: sblocca dopo 10s se Firestore non risponde
    const timeout = setTimeout(() => {
      console.warn("SetupForm queries timeout — sblocco UI dopo 10s");
      setIsLoadingDrivers(false);
      setIsLoadingEvents(false);
    }, 10000);

    const qD = query(collection(db, "drivers"), where("teamId", "==", teamId), orderBy("nomeCompleto", "asc"));
    const unsubD = onSnapshot(qD, (qs) => {
      setDriversList(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingDrivers(false);
    }, (err) => { console.error("Drivers error:", err); setIsLoadingDrivers(false); });

    const qE = query(collection(db, "events"), where("teamId", "==", teamId), orderBy("startDate", "desc"));
    const unsubE = onSnapshot(qE, (qs) => {
      setEventsList(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingEvents(false);
    }, (err) => { console.error("Events error:", err); setIsLoadingEvents(false); });

    return () => { clearTimeout(timeout); unsubD(); unsubE(); };
  }, [teamId]);

  // Carica Sessioni (quando cambia l'evento)
  useEffect(() => {
    if (!selectedEventId) { setEventSessions([]); setIsLoadingSessions(false); return; }
    setIsLoadingSessions(true);
    const sessionsQuery = query(collection(db, "events", selectedEventId, "sessions"), orderBy("startTime", "asc"));
    getDocs(sessionsQuery).then(qs => {
      setEventSessions(qs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }).catch(err => setEventSessions([])).finally(() => setIsLoadingSessions(false));
  }, [selectedEventId]);

  // Logica "Boot": Carica dati (Edit/Duplica) o Imposta Default (Nuovo)
  useEffect(() => {
    const dataIsReady = !isLoadingDrivers && currentVehicle && !isLoadingEvents && !isLoadingVehicleAndTemplate && driversList.length > 0;
    if (!dataIsReady) return;

    const idToLoad = isDuplicating ? duplicateFromId : setupId;

    // CASO 1: MODIFICA O DUPLICA
    if (idToLoad) {
      setIsLoading(true);
      getDoc(doc(db, 'setupSheets', idToLoad)).then(setupSnap => {
        if (setupSnap.exists() && setupSnap.data().teamId === teamId) {
          let data = setupSnap.data();

          if (isDuplicating) {
            data.sessionName = `${data.sessionName || 'Sessione'} - Copia`;
            data.dataSessione = new Date(); // serverTimestamp() è un sentinel Firestore, non convertibile per l'input
            if (data.setupData && data.setupData.feedback) {
              data.setupData.feedback.noteTecnico = `COPIA DA ID: ${idToLoad}\n` + (data.setupData.feedback.noteTecnico || '');
              data.setupData.feedback.bestLap = ''; data.setupData.feedback.listaTempi = '';
              data.setupData.feedback.linkTelemetria = ''; data.setupData.feedback.linkVideo = '';
            }
          }

          setSelectedEventId(data.eventId || '');
          setSelectedSessionId(data.sessionId || '');
          setSelectedSessionName(data.sessionName || '');
          setDataSessione(formatDateForInput(data.dataSessione));

          const foundDriver = data.driverId ? driversList.find(d => d.id === data.driverId) : null;
          setSelectedDriver(foundDriver);

          if (data.eventId && eventsList.length > 0) {
            const foundEvent = eventsList.find(e => e.id === data.eventId);
            setSelectedEvent(foundEvent || null);
          }

          // Deep Merge: unisce lo stato iniziale (con le nuove chiavi) ai dati salvati
          const mergedData = { ...newInitialSetupState };
          for (const key of Object.keys(newInitialSetupState)) {
            if (data.setupData && data.setupData[key]) {
              mergedData[key] = { ...newInitialSetupState[key], ...data.setupData[key] };
            }
          }

          setNewSetupData(mergedData);
        } else {
          navigate(`/vehicle/${vehicleId}`);
        }
      }).catch(err => navigate(`/vehicle/${vehicleId}`))
        .finally(() => setIsLoading(false));
    }
    // CASO 2: NUOVO SETUP
    else {
      setDataSessione(formatDateForInput(new Date()));
      let defaultDriver = null;
      if (currentVehicle.mainDriverId) {
        defaultDriver = driversList.find(d => d.id === currentVehicle.mainDriverId) || null;
        setSelectedDriver(defaultDriver);
      }
      // Imposta i default del veicolo nello stato
      setNewSetupData(prev => ({
        ...prev,
        weights: {
          ...prev.weights,
          dryWeight: currentVehicle.dryWeight || '',
          driverWeight: defaultDriver?.peso || ''
        }
      }));
      setIsLoading(false);
    }
  }, [
    isEditing, isDuplicating, setupId, duplicateFromId, teamId, vehicleId, navigate,
    isLoadingDrivers, currentVehicle, eventsList, isLoadingEvents, driversList, isLoadingVehicleAndTemplate
  ]);

  // Controllo Accesso
  useEffect(() => {
    if (!userRole) return;
    const isManager = userRole === 'admin' || userRole === 'Engineer' || userRole === 'Meccanico';
    if (isManager) {
      setIsAccessBlocked(false);
    } else {
      alert(t('error_access_denied'));
      navigate(`/vehicle/${vehicleId}`);
    }
  }, [userRole, vehicleId, navigate, t]);

  // --- HANDLER EVENTI ---

  const handleEventChange = (event) => {
    const eventId = event.target.value; setSelectedEventId(eventId);
    const foundEvent = eventsList.find(e => e.id === eventId); setSelectedEvent(foundEvent || null);
    if (foundEvent && !dataSessione) { setDataSessione(formatDateForInput(foundEvent.startDate)); }
    setSelectedSessionId(''); setSelectedSessionName('');
  };
  const handleSessionChange = (event) => {
    const sessionId = event.target.value; setSelectedSessionId(sessionId);
    const session = eventSessions.find(s => s.id === sessionId); setSelectedSessionName(session ? session.sessionName : '');
  };
  const handleDriverChange = (event, newValue) => {
    setSelectedDriver(newValue);
    // Aggiorna automaticamente il peso pilota nello stato del setup
    handleChange('weights', 'driverWeight', newValue?.peso || '');
  };

  // Funzione "Master" per aggiornare lo stato (per moduli "Lego" semplici)
  const handleChange = useCallback((section, field, value) => {
    setNewSetupData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  }, []);

  // Funzione per aggiornare sotto-oggetti (es. 'ammortizzatori')
  const handleNestedChange = useCallback((section, subKey, field, value) => {
    setNewSetupData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subKey]: {
          ...prev[section][subKey],
          [field]: value
        }
      }
    }));
  }, []);

  // Funzione specifica per le gomme
  const handleTireChange = useCallback((tireKey, field, value) => {
    setNewSetupData(prev => ({
      ...prev,
      [tireKey]: {
        ...prev[tireKey],
        [field]: value
      }
    }));
  }, []);

  // Funzione per il Meteo
  const handleMeteoChange = (field, value) => {
    handleChange('meteo', field, value);
  };

  // Funzione di Salvataggio
  const handleSaveSetup = async (e) => {
    e.preventDefault();
    if (!selectedEventId || !selectedSessionId || !selectedDriver) {
      alert(t('error_required_fields'));
      return;
    }
    setIsSaving(true);
    const dateObject = new Date(dataSessione); dateObject.setMinutes(dateObject.getMinutes() + dateObject.getTimezoneOffset());

    const setupDoc = {
      teamId: teamId, vehicleId: vehicleId, eventId: selectedEventId,
      eventName: selectedEvent?.eventName || 'Unknown',
      trackName: selectedEvent?.trackName || '',
      sessionId: selectedSessionId, sessionName: selectedSessionName,
      dataSessione: dateObject,
      driverId: selectedDriver.id,
      nomePilota: selectedDriver.nomeCompleto,
      setupData: newSetupData, // Salva il nuovo oggetto dati completo
      lastModified: serverTimestamp()
    };
    try {
      if (isEditing) {
        await updateDoc(doc(db, 'setupSheets', setupId), setupDoc);
      }
      else {
        setupDoc.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, "setupSheets"), setupDoc);
        console.log("Creato nuovo setup con ID: ", docRef.id);
      }
      setIsSaving(false);
      navigate(`/vehicle/${vehicleId}`);
    } catch (error) {
      console.error("Error saving setup:", error);
      setIsSaving(false);
      alert(t('error_save'));
    }
  };

  // --- LOGICA DI RENDER DINAMICO ---

  // Calcola l'helper text per il peso totale (per il modulo Weights)
  const totalCalculatedWeight_HelperText = useMemo(() => {
    const dryWeight = Number(newSetupData.weights.dryWeight || 0);
    const driverWeight = Number(newSetupData.weights.driverWeight || 0);
    const fuelLiters = Number(newSetupData.weights.fuelLiters || 0);
    const fuelWeight = fuelLiters * 0.75;
    const total = dryWeight + driverWeight + fuelWeight;
    return t('field_total_weight_helper', { weight: total.toFixed(1) });
  }, [newSetupData.weights.dryWeight, newSetupData.weights.driverWeight, newSetupData.weights.fuelLiters, t]);


  // --- RENDER ---

  const isPageLoading = isLoading || isAccessBlocked || isLoadingVehicleAndTemplate || isLoadingDrivers || isLoadingEvents;
  if (isPageLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh', gap: 2 }}>
        <CircularProgress />
        <Typography>{t('loading_text')}</Typography>
      </Box>
    );
  }

  if (!setupTemplate) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h5" color="error">{t('error_no_template_title')}</Typography>
        <Typography sx={{ mt: 2 }}>{t('error_no_template_subtitle')}</Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to={`/vehicle/${vehicleId}`}
          sx={{ mt: 2 }}
        >
          {t('error_no_template_button')}
        </Button>
      </Box>
    );
  }

  return (
    <>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton color="inherit" component={RouterLink} to={`/vehicle/${vehicleId}`} sx={{ mr: 2, p: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              {isEditing ? t('form_title_edit') : t('form_title_new')}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {currentVehicle?.marca} {currentVehicle?.modello} ({setupTemplate.templateName})
            </Typography>
          </Box>
        </Box>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSaveSetup}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          size="large"
          sx={{ height: '56px' }}
        >
          {isEditing ? t('save_changes_button') : t('save_button')}
        </Button>
      </Paper>

      {/* Form */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
      >
        <Box component="form" onSubmit={handleSaveSetup}>

          {/* Info Sessione (Sempre presente) */}
          <SetupFormSessionInfo
            t={t}
            eventsList={eventsList}
            isLoadingEvents={isLoadingEvents}
            selectedEventId={selectedEventId}
            handleEventChange={handleEventChange}
            eventSessions={eventSessions}
            isLoadingSessions={isLoadingSessions}
            selectedSessionId={selectedSessionId}
            handleSessionChange={handleSessionChange}
            selectedEvent={selectedEvent}
            currentLang={currentLang}
            dataSessione={dataSessione}
            setDataSessione={setDataSessione}
            driversList={driversList}
            isLoadingDrivers={isLoadingDrivers}
            selectedDriver={selectedDriver}
            handleDriverChange={handleDriverChange}
            meteoData={newSetupData.meteo}
            handleMeteoChange={handleMeteoChange}
          />

          {/* Vista Car-Centric */}
          {setupTemplate?.modules ? (
            <CarCentricFormView
              setupData={newSetupData}
              setupTemplate={setupTemplate}
              handleChange={handleChange}
              handleNestedChange={handleNestedChange}
              handleTireChange={handleTireChange}
              t={t}
              totalCalculatedWeight_HelperText={totalCalculatedWeight_HelperText}
              teamId={teamId}
              userId={auth.currentUser?.uid}
              vehicleId={vehicleId}
              vehicles={currentVehicle ? [currentVehicle] : []}
            />
          ) : (
            <Alert severity="error" sx={{ mt: 3 }}>
              {t('error_invalid_template_format')}
            </Alert>
          )}

          <Button type="submit" variant="contained" color="primary" size="large" disabled={isSaving} startIcon={isSaving ? <CircularProgress size={24} /> : <SaveIcon />} sx={{ mt: 3, height: '56px' }}>
            {isEditing ? t('save_changes_button') : t('save_button')}
          </Button>
        </Box>
      </Paper>
    </>
  );
}

export default SetupForm;